/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import config1 from "../config/inviteUserByEmailForm.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_INVITED_SECTIONS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";

interface Presentation {
  id: string;
  country: string;
  createdAt: string;
  name: string;
}

interface Section {
  id: string;
  name: string;
}

interface SectionOptType {
  name: string;
}

interface InvitedSection {
  presentation: Presentation;
  sections: Section[];
  sectionOptType: SectionOptType[];
  partition: string;
}

interface PartitionOption {
  id: string;
  label: string;
}

interface SectionAndSectionOptType {
  id: string;
  name: string;
  sectionSpecificSectionOptType: SectionOptType[];
}

interface Output {
  presentation: Presentation;
  partitionOption: PartitionOption[];
  defaultSectionOptType: SectionOptType[];
  specialReserveSectionOptType: SectionOptType[];
  sectionsAndSectionOptType: SectionAndSectionOptType[];
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IPresentationListsMethods extends IPluginMethodMap {
  getPresentationListsProps(args: any): Promise<any>;
}

export class PresentationListsService implements IAgentPlugin {
  readonly methods: IPresentationListsMethods;

  constructor() {
    this.methods = {
      getPresentationListsProps: this.getPresentationListsProps.bind(this),
    };
  }

  async getPresentationListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_MY_INVITED_SECTIONS,
          variables: {
            filter: {
              // year: {
              //   eq: "2024",
              // },
              status: {
                eq: "active",
              },
            },
          },
          fetchPolicy: "network-only",
        });

        console.log("output---> input", res);
        return {
          presentationLists: this.generateOutput(
            res?.data?.getMyInvitedSections
          ),
        };
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private generateOutput(input: any[]): any[] {
    console.log("output---> input", input);
    const output: any[] = [];

    if (!Array.isArray(input) || input.length === 0) {
      return output;
    }

    const closedInput = input.filter((item) => item?.inviteType === "Closed");

    if (!Array.isArray(closedInput) || closedInput.length === 0) {
      return output;
    }
    if (!closedInput[0]?.presentation?.id) {
      return output;
    }
    console.log("output---> input 2", closedInput);

    const presentation = closedInput[0].presentation;
    const partitions = [...new Set(closedInput.map((item) => item.partition))];
    const partitionOption = partitions.map((partition) => ({
      id: partition,
      label: partition,
    }));

    output.push({
      id: presentation.id,
      createdAt: presentation.createdAt,
      name: presentation.name,
      partitionOption,
      defaultSectionOptType: this.getUniqueSectionOptTypes(closedInput, "Default"),
      specialReserveSectionOptType: this.getUniqueSectionOptTypes(
        closedInput,
        "SpecialReserve"
      ),
      sectionsAndSectionOptType: this.getSectionsAndSectionOptType(
        closedInput,
        "SectionSpecific"
      ),
    });
    console.log("output---> output", output);
    return output;
  }

  getUniqueSectionOptTypes(input: any[], partition: string): any[] {
    const sectionOptTypes = input
      .filter((item) => item.partition === partition)
      .flatMap((item) => item.sectionOptType)
      .map((optType) => optType.name);

    return [...new Set(sectionOptTypes)].map((name) => ({
      id: name,
      label: name,
    }));
  }

  getSectionsAndSectionOptType(input: any[], partition: string): any[] {
    const output: any[] = [];

    input.forEach((item) => {
      if (item.partition === partition) {
        const section = item.sections[0];

        if (!section) {
          return;
        }

        const sectionOptType = item.sectionOptType.map((optType) => ({
          id: optType.name,
          label: optType.name,
        }));

        const existingSection = output.find((sec) => sec.id === section.id);

        if (existingSection) {
          if (existingSection.sectionSpecificSectionOptType) {
            existingSection.sectionSpecificSectionOptType.push(
              ...sectionOptType
            );
          } else {
            existingSection.sectionSpecificSectionOptType = sectionOptType;
          }
        } else {
          output.push({
            id: section.id,
            name: section.name,
            sectionSpecificSectionOptType: sectionOptType,
          });
        }
      }
    });
    return output;
  }

}
